import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {FirebaseService} from '../../../shared-utilities/services-old/firebase.service';
import * as NGPReportActions from './ngp-report.actions';
import {catchError, filter, map, mergeMap, switchMap, take, tap, withLatestFrom} from 'rxjs/operators';
import {EMPTY, forkJoin, from, of} from 'rxjs';
import {
  sItemKeyToInt,
  StockItem,
  Supplier,
  VatRates,
} from '../../../shared-utilities/models-old/datastructures';
import {IFirebaseQuery} from '../../../shared/shared-models/firebase/firebase-queries';
import {IDisableStockItemRule} from '../../settings-new/models';
import {Action, select, Store} from '@ngrx/store';
import {NGPReport} from '../../../shared-utilities/models-old/ngp-reports/ngp-report';
import {
  selectAllVatRates,
  selectItemDisablingRules,
  selectNGPReports,
  selectNGPState,
  selectSuppliersByUserSelectedStore,
  selectVatRateBySelectedStore,
} from './ngp-report.selectors';
import {IDepartment} from '../../../shared/shared-models/stock/departments';
import {IStore} from '../../../shared/shared-models/store/store';
import {ReportUtils} from '../../../shared-utilities/utils-old/shared-utils-old/report-utils';
import {CollectionSharedStockService} from '../../../shared/shared-services/firebase/collection-shared-stock.service';
import {selectNGPReportsPriceBanded} from '../../../shared-modules/shared-grid/store/shared-grid.selectors';
import {
  CollectionUsersSettingsService,
} from '../../../shared/shared-services/firebase/collection-users-settings.service';
import {
  path_stores_storeId_settings_disable_stock_item_rules,
  path_users_userId_settings_ngp_edits_preview,
} from '../../../shared/shared-services/database-paths';
import {getDeepCopyOfObject} from '../../../shared/shared-utils/object/object.utils';
import {
  CollectionStoresSettingsService,
} from '../../../shared/shared-services/firebase/collection-stores-settings.service';
import {
  clearEditedItemsInLocalStorage,
  removeNewlyRemovedItemsFromNgpReports,
  updateEditedItemsInLocalStorage,
} from './ngp-report.effects.utils';
import {ISharedStoreSettings} from '../../../shared/shared-models/firebase/shared-store-settings';
import {
  CollectionSharedSingularDocumentsService,
} from '../../../shared/shared-services/firebase/collection-shared-singular-documents.service';
import {StockFunctions} from '../../../shared-utilities/functions-old/stock-functions';
import {FiltersAndTools} from '../../../shared-utilities/models-old/ngp-reports/filters-tools-ngp';
import {FilterAndToolUtils} from '../../../shared-utilities/models-old/utils-old/filterAndToolUtils';
import * as UserAccessSelectors from '../../user-access/user-store/user.selectors';
import * as UserStoreSelectors from '../../user-access/user-stores-store/user-store.selectors';
import {selectSelectedStore, selectStores} from '../../user-access/user-stores-store/user-store.selectors';
import * as UserStoreActions from '../../user-access/user-stores-store/user-store.actions';
import {subQueryStock} from '../../../shared-utilities/queries-old/sub-query-stock-2';
import {selectItemEnablingSettings} from '../../settings/store/settings.selectors';
import {createSharedPaths} from '../../../shared/shared-services/database-paths.shared';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';
import {ErrorName} from '../../../shared/shared-components/components/global-error-display/models/global-error.enum';
import * as NotificationActions from '../../notifications/store/notification.actions';
import {TLineColour} from '../../settings-new/models';
import {NgpReportStoreSyncReason} from '../models/ngp-report-store-sync-reason.enum';
import {GridHeaderMenuActionTypes} from '../../../shared/shared-models/grid-menu/header-menu-action-types';
import {GridHeaderMenuTypeChange} from '../../../shared/shared-models/grid-menu/header-menu-type-changes';
import {getTableSettings} from '../../settings/store/settings.actions';
import {getPriceBandingForStores} from '../../../shared-modules/shared-grid/store/shared-grid.actions';
import {HeaderMenuColumnData} from '../../../shared-utilities/models-old/ngp-report-grid/header-menu-data';

@Injectable()
export class NgpReportEffects {
  // ====================================================================================================
  // Get Line Colours
  // ====================================================================================================
  getLineColours$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.getLineColours),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        from(this.firebaseService.getStoreDataDoc(action.pageStoreDocument, store.storeId,
        )).pipe(
          switchMap((lineColours: TLineColour) =>
            from(
              this.collectionSharedSingularDocumentsService.getDocument(
                createSharedPaths().setStoreId(store.storeId).storeSettings(),
              ),
            ).pipe(
              map((storeSettings: ISharedStoreSettings) =>
                NGPReportActions.getLineColoursSuccess({
                  lineColours: storeSettings.lineColoursEnabled ? lineColours : {},
                  selectedStore: store,
                  storeSettings,
                }),
              ),
              catchError((error) =>
                of(NGPReportActions.getLineColoursFailure({
                  error: {
                    ...error,
                    action: JSON.stringify(action),
                    actionType: action.type,
                    originalError: error,
                    storeIds: [store?.storeId ?? 'unknown'],
                    userId: user?.id ?? 'unknown',
                  },
                })),
              ),
            ),
          ),
          catchError((error) =>
            of(NGPReportActions.getLineColoursFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store?.storeId ?? 'unknown'],
                userId: user?.id ?? 'unknown',
              },
            })),
          ),
        ),
      ),
    ),
  );


  // ====================================================================================================
  // Get Stock Items
  // ====================================================================================================
  getStockItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.getStockItem),
      withLatestFrom(this.store.pipe(select(selectSuppliersByUserSelectedStore))),
      switchMap(([getStockItemAction, selectedStoreSuppliers]) => {
        const actions: Action[] = [];
        if (Object.keys(selectedStoreSuppliers).length > 0) {
          actions.push(
            NGPReportActions.getStockItemWithExistingSuppliers({
              ...getStockItemAction,
              suppliers: selectedStoreSuppliers,
            }),
          );
        } else {
          actions.push(NGPReportActions.getStockItemWithoutExistingSuppliers(getStockItemAction));
        }
        return actions;
      }),
    ),
  );

  // '[NGP Report] Get Stock Item With Existing Suppliers'
  getStockItemWithExistingSuppliers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.getStockItemWithExistingSuppliers),
      withLatestFrom(
        this.store.pipe(select(selectVatRateBySelectedStore)),
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, vatRates, store, user]) =>
        this.collectionSharedStockService.getStockItemsCast(
          action.store,
          action.firebaseQueries,
          action.idField,
        ).pipe(
          map((stockItemsRaw: StockItem[]) => {
            const stockItems = stockItemsRaw.map((item: StockItem): StockItem => ({
              ...item,
              newlyAdded: true,
              storeId: store.storeId,
              store: store.name,
              suppName: action.suppliers[item.regularSuppCode]?.name || null,
            }));
            return NGPReportActions.getStockItemSuccess({stockItems, store, vatRates});
          }),
          catchError((error) => of(
            NGPReportActions.getStockItemFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store?.storeId ?? 'unknown'],
                userId: user?.id ?? 'unknown',
              },
            })),
          ),
        ),
      ),
    ),
  );

  clearNGPReportStockItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.clearNGPReportStockItems),
      withLatestFrom(this.store.pipe(select(selectSelectedStore))),
      mergeMap(async ([_, store]) => NGPReportActions.getStockItem({
          store,
          firebaseQueries: subQueryStock,
          idField: 'objectID',
        }),
      ),
    ),
  );

  // '[NGP Report] Get Stock Item Without Existing Suppliers'
  getStockItemWithoutExistingSuppliers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.getStockItemWithoutExistingSuppliers),
      withLatestFrom(
        this.store.pipe(select(selectVatRateBySelectedStore)),
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, vatRates, store, user]) =>
        this.collectionSharedStockService.getStockItemsCast(
          action.store,
          action.firebaseQueries,
          action.idField,
        ).pipe(
          switchMap((ngpReports: StockItem[]) =>
            from(this.firebaseService.getSuppliers(action.store.storeId)).pipe(
              switchMap((rawSuppliers: Record<string, Supplier>) => {
                const suppliers = this.createSupplierMap(rawSuppliers);

                const stockItems = ngpReports.map((item: StockItem): StockItem => ({
                  ...item,
                  newlyAdded: true,
                  storeId: action.store.storeId,
                  store: action.store.name,
                  suppName: suppliers[item.regularSuppCode]?.name ?? null,
                }));

                return from([
                  NGPReportActions.getStoreSuppliersByStoreIdSuccess({
                    suppliers,
                    storeId: action.store.storeId,
                  }),
                  NGPReportActions.getStockItemSuccess({
                    stockItems,
                    store: action.store,
                    vatRates,
                  }),
                ]);
              }),
              catchError((error) =>
                of(
                  NGPReportActions.getStockItemFailure({
                    error: {
                      ...error,
                      action: JSON.stringify(action),
                      actionType: action.type,
                      originalError: error,
                      storeIds: [store?.storeId ?? 'unknown'],
                      userId: user?.id ?? 'unknown',
                    },
                  }),
                  NGPReportActions.getStoreSuppliersByStoreIdFailure({
                    error: {
                      ...error,
                      action: JSON.stringify(action),
                      actionType: action.type,
                      originalError: error,
                      storeIds: [store?.storeId ?? 'unknown'],
                      userId: user?.id ?? 'unknown',
                    },
                  }),
                ),
              ),
            ),
          ),
        ),
      ),
    ),
  );

  // [NGP Report][Stock Item] Get Stock Items For NGP Preview (scoped by stock codes)
  getStockItemsForNgpPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.getStockItemsForNgpPreview),
      withLatestFrom(
        this.store.pipe(select(selectVatRateBySelectedStore)),
        this.store.pipe(select(selectSuppliersByUserSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      switchMap(([action, vatRates, suppliers, user]) => {
        const {store, stockCodes} = action;
        const chunkSize = 30;
        const chunks: string[][] = [];
        for (let i = 0; i < stockCodes.length; i += chunkSize) {
          chunks.push(stockCodes.slice(i, i + chunkSize));
        }

        if (chunks.length < 1) {
          return of(NGPReportActions.getStockItemsForNgpPreviewSuccess({stockItems: [], store, vatRates}));
        }

        const chunkRequests = chunks.map((codes: string[]) => {
          const query: IFirebaseQuery[] = [
            {q: 'where', p: [sItemKeyToInt.code, 'in', codes]},
          ];
          return this.collectionSharedStockService.getStockItemsCast(store, query, 'objectID').pipe(take(1));
        });

        return forkJoin(chunkRequests).pipe(
          map((chunkResults: StockItem[][]) => {
            const stockItems = chunkResults.flat().map((item: StockItem): StockItem => ({
              ...item,
              storeId: store.storeId,
              store: store.name,
              suppName: suppliers[item.regularSuppCode]?.name ?? null,
            }));
            return NGPReportActions.getStockItemsForNgpPreviewSuccess({stockItems, store, vatRates});
          }),
          catchError((error) =>
            of(NGPReportActions.getStockItemsForNgpPreviewFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store.storeId],
                userId: user?.id ?? 'unknown',
              },
            })),
          ),
        );
      }),
    ),
  );

  // ====================================================================================================
  // Get Items Disabling Rules
  // ====================================================================================================
  getStockItemDisablingRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.getStockItem, NGPReportActions.getStockItemDisablingRules),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        from(
          this.collectionStoresSettingsService.getDocument<IDisableStockItemRule>(
            path_stores_storeId_settings_disable_stock_item_rules(store.storeId),
          ),
        ).pipe(
          map((rulesDoc: IDisableStockItemRule) =>
            NGPReportActions.getStockItemDisablingRulesSuccess({store: store, rulesDoc}),
          ),
          catchError((error) =>
            of(NGPReportActions.getStockItemDisablingRulesFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store?.storeId ?? 'unknown'],
                userId: user?.id ?? 'unknown',
              },
            })),
          ),
        ),
      ),
    ),
  );

  // ====================================================================================================
  // Set Disabled/Enabled items
  // ====================================================================================================
  setItemDisabled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.setItemDisabledStatus),
      withLatestFrom(
        this.store.pipe(select(selectSelectedStore)),
        this.store.pipe(select(selectItemDisablingRules)),
        this.store.pipe(select(selectItemEnablingSettings)),
      ),
      switchMap(([action, currentSelectedStore, disabledRules, enabledRules]) => {
        const updatedNgpReport = {...action.ngpReport};
        ReportUtils.enableReport(updatedNgpReport, disabledRules, enabledRules.rulesDoc, action.enable);
        return [
          NGPReportActions.updateSingleNGPReportWithStoreId({
            ngpReport: updatedNgpReport,
            storeId: currentSelectedStore.storeId,
          }),
        ];
      }),
    ),
  );

  // ====================================================================================================
  // Set Filters and Tools
  // ====================================================================================================
  setNGPReportFiltersAndToolsWithStore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.setNGPReportFiltersAndTools),
      withLatestFrom(this.store.pipe(select(selectSelectedStore))),
      map(([filtersAndTools, store]) =>
        NGPReportActions.setNGPReportFiltersAndToolsWithStore({
          filtersAndTools: filtersAndTools.filtersAndTools,
          store,
        }),
      ),
    ),
  );

  // ====================================================================================================
  // Set Grid Menu Action
  // ====================================================================================================
  setNGPReportMenuActionsWithStore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.setNGPReportMenuActions),
      withLatestFrom(this.store.pipe(select(selectSelectedStore))),
      switchMap(([action, userStores]) => {
        if (action.gridHeaderMenu.typeOfChange === 'isSorting' && (action.gridHeaderMenu.colId === 'depName' || action.gridHeaderMenu.colId === 'subDepName')) {
          return EMPTY;
        }
        return of(NGPReportActions.setNGPReportMenuActionsWithStore({
          ...action,
          store: userStores,
        }));
      }),
    ),
  );

  // ====================================================================================================
  // Store Change
  // ====================================================================================================
  ngpReportWatchStoreContext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        UserStoreActions.setSelectedStoreSuccess,
        UserStoreActions.getStoresInformationSuccess,
        NGPReportActions.ngpReportInitializePage,
      ),
      withLatestFrom(
        this.store.pipe(select(selectSelectedStore)),
        this.store.pipe(select(selectNGPState)),
      ),
      mergeMap(([action, selectedStore, ngpState]) => {
        if (!ngpState.isNgpReportPageActive || !selectedStore?.storeId) {
          return EMPTY;
        }

        let reason: NgpReportStoreSyncReason;
        if (action.type === NGPReportActions.ngpReportInitializePage.type) {
          reason = NgpReportStoreSyncReason.PageEnter;
        } else if (action.type === UserStoreActions.setSelectedStoreSuccess.type) {
          reason = NgpReportStoreSyncReason.StoreSelected;
        } else {
          reason = NgpReportStoreSyncReason.StoresLoaded;
        }

        const isPageEnter: boolean = reason === NgpReportStoreSyncReason.PageEnter;
        if (!isPageEnter && ngpState.loadedStoreId === selectedStore.storeId) {
          return EMPTY;
        }

        const previousLoadedStoreId: string | null =
          reason === NgpReportStoreSyncReason.StoreSelected
            ? (ngpState.loadedStoreId ?? ngpState.observedSelectedStoreId)
            : ngpState.loadedStoreId;

        const actions: Action[] = [];

        if (
          ngpState.observedSelectedStoreId === null &&
          reason !== NgpReportStoreSyncReason.StoreSelected
        ) {
          actions.push(
            NGPReportActions.ngpReportObservedSelectedStoreSeeded({storeId: selectedStore.storeId}),
          );
        }

        actions.push(
          NGPReportActions.ngpReportSyncStoreContext({
            store: selectedStore,
            previousLoadedStoreId,
            reason,
          }),
        );

        return from(actions);
      }),
    ),
  );

  ngpReportSyncStoreContext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.ngpReportSyncStoreContext),
      withLatestFrom(this.store.pipe(select(selectNGPState))),
      mergeMap(([action, ngpState]) => {
        const {store, previousLoadedStoreId} = action;

        StockFunctions.temporaryVatInit(this.firebaseService, store.storeId);

        const actions: Action[] = [
          NGPReportActions.getStockItemDisablingRules(),
          getTableSettings(),
          getPriceBandingForStores(),
          NGPReportActions.getStoreDepartments({store}),
          NGPReportActions.getLineColours({pageStoreDocument: 'line_colours'}),
          NGPReportActions.getStockItem({
            store,
            firebaseQueries: subQueryStock,
            idField: 'objectID',
          }),
        ];

        const shouldCleanupMenu: boolean =
          previousLoadedStoreId !== null && previousLoadedStoreId !== store.storeId;

        if (shouldCleanupMenu) {
          const previousMenuData: Record<string, HeaderMenuColumnData> =
            ngpState.headerMenuData[previousLoadedStoreId] ?? {};
          const currentMenuData: Record<string, HeaderMenuColumnData> =
            ngpState.headerMenuData[store.storeId] ?? {};
          const fieldsToReset: string[] = [];

          Object.keys(previousMenuData).forEach((item: string) => {
            fieldsToReset.push(item);
            if (!currentMenuData[item]) {
              actions.push(
                NGPReportActions.setNGPReportMenuActionsWithStore({
                  store,
                  gridHeaderMenu: {
                    action: GridHeaderMenuActionTypes.editing,
                    colId: item,
                    typeOfChange: GridHeaderMenuTypeChange.isEditing,
                    value: false,
                  },
                }),
              );
            }
          });

          if (fieldsToReset.length > 0) {
            actions.push(NGPReportActions.ngpReportColumnEditingResetRequested({fields: fieldsToReset}));
          }
        }

        actions.push(NGPReportActions.ngpReportStoreContextSynced({storeId: store.storeId}));

        return from(actions);
      }),
    ),
  );

  // ====================================================================================================
  // Get Store Departments
  // ====================================================================================================
  fetchStoreDepartments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.getStoreDepartments),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        this.firebaseService.getDepartmentsByStore(action.store).pipe(
          map((departments: IDepartment[]) =>
            NGPReportActions.getStoreDepartmentsSuccess({
              store: action.store,
              departments,
            }),
          ),
          catchError((error) => of(
            NGPReportActions.getStoreDepartmentsFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store?.storeId ?? 'unknown'],
                userId: user?.id ?? 'unknown',
              },
            })),
          ),
        ),
      ),
    ),
  );

  // ====================================================================================================
  // Remove Selected Items From NGP Report
  // ====================================================================================================
  // Unhandled Error: FirebaseError: Failed to union tags in_ngp to codes  on store M1ql75dm22Xcyc5Ri6ZY.
  // No document to update: projects/gallix-qa/databases/(default)/documents/shared/stores_data/M1ql75dm22Xcyc5Ri6ZY/data/stock/TEST/%5C/%5C/%5C/TEST

  applyAutoOrderStockEdits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.applyAutoOrderStockEdits),
      map(({editedItems, itemsToUpdate, taggedItems}) =>
        NGPReportActions.removeSelectedNgpReports({
          selectedReports: [],
          editedItems,
          itemsToUpdate,
          taggedItems: taggedItems ?? {},
        }),
      ),
    ),
  );

  removeSelectedNgpReports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.removeSelectedNgpReports),
      withLatestFrom(
        this.store.pipe(select(selectSelectedStore)),
        this.store.pipe(select(selectNGPReports)),
      ),
      mergeMap(([{selectedReports, itemsToUpdate, editedItems, taggedItems}, store, currentNgpReport]) => {
        const tagsToWrite: Record<string, string[]> = {...(taggedItems ?? {})};

        const persistChanges = async (): Promise<string[]> => {
          const flaggedItems: string[] = [];
          if (Object.keys(itemsToUpdate).length) {
            await this.firebaseService.stockUpdate(store.storeId, itemsToUpdate);
          }
          flaggedItems.forEach((item: string) => {
            delete tagsToWrite[item];
          });
          if (Object.keys(tagsToWrite).length > 0) {
            await this.collectionSharedStockService.updateStockTags(tagsToWrite, store);
          }
          return flaggedItems;
        };

        return from(persistChanges()).pipe(
          mergeMap((flaggedItems: string[]) => {
            const writtenStockIds = new Set(Object.keys(tagsToWrite));
            const reportsToRemove = selectedReports.filter((report: NGPReport) =>
              writtenStockIds.has(report.stockId),
            );

            let allNgpReports: NGPReport[] = [];
            const editedNgpReports = getDeepCopyOfObject(editedItems);
            editedNgpReports.forEach((item: NGPReport) => {
              item.originalValue = getDeepCopyOfObject(item.originalValue);
              Object.keys(item).forEach((key: string) => {

                if (item.originalValue?.[key]?.value) {
                  item.originalValue[key].value = item[key];
                }

                item.isEdited = false;
              });
            });

            const storeReportsMap = currentNgpReport.get(store.storeId) || new Map();
            const flaggedReportsMap = new Map<string, NGPReport>(
              flaggedItems
                .filter(code => storeReportsMap.has(code))
                .map(code => [code, storeReportsMap.get(code)]),
            );
            const mergedReportsMap = new Map<string, NGPReport>(storeReportsMap);

            editedNgpReports.forEach((report: NGPReport) => {
              mergedReportsMap.set(report.stockId, report);
            });
            allNgpReports = Array.from(mergedReportsMap.values());

            this.store.dispatch(
              NGPReportActions.updateAllNGPReports({
                ngpReports: [...allNgpReports],
              }),
            );

            this.store.dispatch(NGPReportActions.clearSavedNgpReportEditsForStore());

            const singleUpdateActions = Array.from(flaggedReportsMap.values()).map(report =>
              NGPReportActions.updateSingleNGPReport({ngpReport: report}),
            );

            return from([
              NGPReportActions.removeSelectedNgpReportsWithStoreID({
                selectedReports: reportsToRemove,
                storeId: store.storeId,
              }),
              ...singleUpdateActions,
            ]);
          }),
          catchError((error: unknown) => {
            const nestedError = (error as {error?: {message?: string; name?: string}}).error;
            const message = (error as {message?: string}).message || nestedError?.message || '';
            const cancelled = message === 'Update Cancelled';
            const globalError: IGlobalError = {
              action: '[NGP Report] Remove Selected Items',
              actionType: NGPReportActions.removeSelectedNgpReports.type,
              message: cancelled
                ? 'Update Cancelled'
                : 'Could not remove the items from the NGP report. They are still on the report so you can try again.',
              name: cancelled ? 'User Error' : ErrorName.ActionFailedError,
              originalError: error,
            };
            if (cancelled) {
              return of(NGPReportActions.removeSelectedNgpReportsFailure({error: globalError}));
            }
            return of(
              NGPReportActions.removeSelectedNgpReportsFailure({error: globalError}),
              NotificationActions.setGlobalErrorOccurrence({error: globalError}),
            );
          }),
        );
      }),
    ),
  );

  updateSingleNGPReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.updateSingleNGPReport),
      withLatestFrom(this.store.pipe(select(selectSelectedStore))),
      switchMap(([action, currentSelectedStore]) => {
        const {ngpReport} = action;
        return [
          NGPReportActions.updateSingleNGPReportWithStoreId({
            ngpReport: ngpReport,
            storeId: currentSelectedStore.storeId,
          }),
          NGPReportActions.saveSingleNgpItemToLocalStorage({ngpReport: ngpReport}),
        ];
      }),
    ),
  );

  // '[NGP Report][Update] Save Single Ngp Item To Local Storage'
  saveSingleNgpItemToLocalStorage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          NGPReportActions.saveSingleNgpItemToLocalStorage,
          NGPReportActions.updatePriceGpForNgpReportWithStoreIDSuccess),
        withLatestFrom(this.store.pipe(select(selectSelectedStore))),
        tap(([{ngpReport}, store]) => {
          if (!ngpReport?.stockId) {
            console.warn('Invalid NGPReport or missing stockId:', ngpReport);
            return;
          }
          updateEditedItemsInLocalStorage(ngpReport, store, 'savedNgpReportEdits');
        }),
        catchError((error: IGlobalError) => {
          return of();
        }),
      ),
    {dispatch: false},
  );

  // [NGPReport] Clear Saved Edits For Store
  clearSavedNgpReportEditsForStore$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NGPReportActions.clearSavedNgpReportEditsForStore),
        withLatestFrom(this.store.pipe(select(selectSelectedStore))),
        tap(([_, store]) => {
          clearEditedItemsInLocalStorage(store, 'savedNgpReportEdits');
        }),
      ),
    {dispatch: false},
  );

  // '[NGP Report][Price Banding] Update The Price NGP and GpDiff for NGP Reports With PriceBanding'
  setApplyPriceBandingToNGPReportItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.setApplyPriceBandingToNGPReportItems),
      withLatestFrom(
        this.store.pipe(select(selectSelectedStore)),
        this.store.pipe(select(selectNGPReportsPriceBanded)),
      ),
      switchMap(([_, currentSelectedStore, ngpReports]) => {
        const ngpReportsByStockId = ngpReports.reduce(
          (acc: Record<string, NGPReport>, ngpReport: NGPReport) => {
            acc[ngpReport.stockId] = ngpReport;
            return acc;
          },
          {},
        );
        return [
          NGPReportActions.setApplyPriceBandingToNGPReportItemsAtStoreId({
            ngpReports: ngpReportsByStockId,
            store: currentSelectedStore,
          }),
        ];
      }),
    ),
  );

  updateIsSelectedForSingleNGPReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.updateIsSelectedForSingleNGPReport),
      withLatestFrom(
        this.store.pipe(select(selectSelectedStore)),
        this.store.pipe(select(selectNGPReports)),
      ),
      switchMap(([action, currentSelectedStore, mostRecentNGPReport]) => {
        const storeMap = mostRecentNGPReport.get(currentSelectedStore.storeId);
        const matchingNGPItem = storeMap ? storeMap.get(action.ngpReport.stockId) : undefined;
        if (!matchingNGPItem) return EMPTY;
        const updatedNGPItem = {...matchingNGPItem, isSelected: action.ngpReport.isSelected};
        return [
          NGPReportActions.updateIsSelectedForSingleNGPReportWithStoreId({
            ngpReport: updatedNGPItem,
            storeId: currentSelectedStore.storeId,
          }),
        ];
      }),
    ),
  );

  updateAllNGPReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.updateAllNGPReports),
      withLatestFrom(this.store.pipe(select(selectSelectedStore))),
      switchMap(([action, currentSelectedStore]) => {
        return [
          NGPReportActions.updateAllNGPReportsWithStoreID({
            ngpReports: [...action.ngpReports],
            storeId: currentSelectedStore.storeId,
          }),
        ];
      }),
    ),
  );

  updateNewlyAddedItemsVisibility$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.setNewlyAddedItemsVisibility),
      withLatestFrom(this.store.pipe(select(selectSelectedStore))),
      map(([_, currentSelectedStore]) =>
        NGPReportActions.setNewlyAddedItemsVisibilityWithStoreID({
          storeId: currentSelectedStore.storeId,
        }),
      ),
    ),
  );

  updateNewlyRemovedItemsVisibility$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.setNewlyRemovedItemsVisibility),
      withLatestFrom(
        this.store.pipe(select(selectSelectedStore)),
        this.store.pipe(select(selectNGPState)),
      ),
      map(([_, currentSelectedStore, state]) => {
        const storeId = currentSelectedStore.storeId;
        const storeReports: Map<string, NGPReport> = state.ngpReports.get(storeId) || new Map();
        const newlyRemoved: Map<string, NGPReport> = state.ngpReportsNewlyRemoved.get(storeId) || new Map();
        const updatedStoreReports = removeNewlyRemovedItemsFromNgpReports(storeReports, newlyRemoved);

        let filtersAndTools: FiltersAndTools = state.filtersAndTools;
        if (updatedStoreReports.size > 0) {
          filtersAndTools = FilterAndToolUtils.determineFiltersAndTools(
            Array.from(updatedStoreReports.values()),
            filtersAndTools,
          );
          filtersAndTools = FilterAndToolUtils.changeFilterAndToolsCounts(filtersAndTools);
        }

        const copiedMenuData = getDeepCopyOfObject(state.headerMenuData);
        Object.keys(state.headerMenuData).forEach((sId: string) => {
          Object.keys(state.headerMenuData[sId]).forEach((colId: string) => {
            delete copiedMenuData[sId][colId].sortType;
          });
        });

        filtersAndTools.total =
          (state.ngpReportsNewlyAdded.get(storeId)?.size || 0) + (state.ngpReports.get(storeId)?.size || 0);

        const newNgpReportsNewlyRemoved = new Map(state.ngpReportsNewlyRemoved);
        newNgpReportsNewlyRemoved.set(storeId, new Map());

        const newNgpReports = new Map(state.ngpReports);
        newNgpReports.set(storeId, updatedStoreReports);

        return NGPReportActions.setNewlyRemovedItemsVisibilitySuccess({
          filtersAndTools,
          headerMenuData: copiedMenuData,
          ngpReports: newNgpReports,
          ngpReportsNewlyRemoved: newNgpReportsNewlyRemoved,
        });
      }),
    ),
  );


  updateNGPReportGridColDefs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.setNGPReportGridColDefs),
      withLatestFrom(this.store.pipe(select(selectSelectedStore))),
      map(([action, currentSelectedStore]) =>
        NGPReportActions.setNGPReportGridColDefsWithStoreID({
          colDefs: action.colDefs,
          storeId: currentSelectedStore.storeId,
        }),
      ),
    ),
  );

  setNGPReportVisibility$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.setNGPReportVisibility),
      withLatestFrom(this.store.pipe(select(selectSelectedStore))),
      map(([action, currentSelectedStore]) =>
        NGPReportActions.setNGPReportVisibilityWithStoreID({
          colDef: action.colDef,
          menuData: action.menuData,
          storeId: currentSelectedStore.storeId,
        }),
      ),
    ),
  );

  updatePriceGpForNgpReportWithStoreId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.updatePriceGpForNgpReport),
      withLatestFrom(this.store.pipe(select(selectSelectedStore)),
        this.store.pipe(select(selectVatRateBySelectedStore))),
      switchMap(([action, currentSelectedStore, vatRates]) => {
        const {field, ngpReport} = action;
        const {storeId} = currentSelectedStore;
        ngpReport.prevCostPrice = +ngpReport.originalValue['prevCostPrice'].value;

        switch (field) {
          case 'sellPriIncl1':
            const nomGPCalculated = +StockFunctions.calcGP(storeId, ngpReport as unknown as StockItem, ngpReport.sellPriIncl1).toFixed(2);
            const diffGP = nomGPCalculated === ngpReport.nominalGP ? ngpReport.diffGP : nomGPCalculated - ngpReport.nominalGP;
            ngpReport.nominalGP = nomGPCalculated;
            ngpReport.diffGP = diffGP;
            if (nomGPCalculated < 0.01) {
              ngpReport.sellPriIncl1 = ReportUtils.calculatePriceFromGP(storeId, ngpReport, vatRates);
              ngpReport.nominalGP = +StockFunctions.calcGP(
                storeId,
                ngpReport as unknown as StockItem,
                ReportUtils.calculatePriceFromGP(storeId, ngpReport, vatRates),
              ).toFixed(2);
            }
            break;
          case 'nominalGP':
            const newPrice = ReportUtils.calculatePriceFromGP(storeId, ngpReport, vatRates);
            ngpReport.sellPriIncl1 = newPrice;
            const nomGpCalculated = +StockFunctions.calcGP(storeId, ngpReport as unknown as StockItem, newPrice).toFixed(2);
            ngpReport.nominalGP = nomGpCalculated;
            ngpReport.diffGP = nomGpCalculated - +ngpReport.originalValue.nominalGP.value;
            break;
        }
        return [
          NGPReportActions.updatePriceGpForNgpReportWithStoreIDSuccess({
            ngpReport,
            field: field,
            storeId: storeId,
          }),
        ];
      }),
    ),
  );

  getVatRateConversion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.getVatRateConversion),
      withLatestFrom(
        this.store.pipe(select(selectStores)),
        this.store.pipe(select(selectAllVatRates)),
      ),
      switchMap(([action, userStores, vatRates]) => {
        const actions: Action[] = [];
        if (Object.keys(vatRates).length > 0) {
          actions.push(
            NGPReportActions.getVatRateConversionWithExistingVatRates({
              userStores,
              vatRates: vatRates,
            }),
          );
        } else {
          actions.push(NGPReportActions.getVatRateConversionWithoutExistingVatRates({userStores}));
        }
        return actions;
      }),
    ),
  );

  getVatRateConversionWithExistingVatRates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.getVatRateConversionWithExistingVatRates),
      map(({userStores, vatRates}) => {
        const vatRatesResult = userStores.reduce((acc, userStore) => {
          acc[userStore.storeId] = vatRates[userStore.storeId];
          return acc;
        }, {});
        return NGPReportActions.getVatRateConversionSuccess({vatRates: vatRatesResult});
      }),
    ),
  );

  getVatRateConversionWithoutExistingVatRates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.getVatRateConversionWithoutExistingVatRates),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      switchMap(([action, store, user]) => {
        const vatRatesObservables = action.userStores.map((userStore: IStore) =>
          this.firebaseService
            .subStoreDoc('data/singular_documents/vat_rates', userStore.storeId)
            .pipe(
              take(1),
              map((vr: VatRates) => ({storeId: userStore.storeId, vatRate: vr})),
            ),
        );
        return forkJoin(vatRatesObservables).pipe(
          map((vatRatesArray) => {
            const vatRates = vatRatesArray.reduce((acc, {storeId, vatRate}) => {
              acc[storeId] = vatRate;
              return acc;
            }, {});
            return NGPReportActions.getVatRateConversionSuccess({vatRates});
          }),
          catchError((error) => of(
            NGPReportActions.getVatRateConversionFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store?.storeId ?? 'unknown'],
                userId: user?.id ?? 'unknown',
              },
            })),
          ),
        );
      }),
    ),
  );

  // ====================================================================================================
  // Set the NGP Preview Column Data in firebase
  // ====================================================================================================
  setNgpPreviewColumns$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.setNgpPreviewColumns),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      switchMap(([action, store, user]) => {
        return from(
          this.collectionUsersSettingsService.setDocument<Record<number, string>>(
            path_users_userId_settings_ngp_edits_preview(user.id),
            action.columnData,
          ),
        ).pipe(
          map(() => NGPReportActions.setNgpPreviewColumnsSuccess({columnData: action.columnData})),
          catchError((error) => of(
            NGPReportActions.setNgpPreviewColumnsFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store?.storeId ?? 'unknown'],
                userId: user?.id ?? 'unknown',
              },
            })),
          ),
        );
      }),
    ),
  );

  getNgpPreviewColumns$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NGPReportActions.getNgpPreviewColumns),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      switchMap(([action, store, user]) => {
        return from(
          this.collectionUsersSettingsService.getDocument<Record<number, string>>(
            path_users_userId_settings_ngp_edits_preview(user.id),
          ),
        ).pipe(
          map((columnData: Record<number, string> | undefined) =>
            NGPReportActions.setNgpPreviewColumnsSuccess({columnData: columnData ? columnData : {}}),
          ),
          catchError((error) => of(
            NGPReportActions.setNgpPreviewColumnsFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store?.storeId ?? 'unknown'],
                userId: user?.id ?? 'unknown',
              },
            })),
          ),
        );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private firebaseService: FirebaseService,
    private collectionSharedStockService: CollectionSharedStockService,
    private collectionStoresSettingsService: CollectionStoresSettingsService,
    private collectionUsersSettingsService: CollectionUsersSettingsService,
    private collectionSharedSingularDocumentsService: CollectionSharedSingularDocumentsService,
    private readonly store: Store,
  ) {
  }

  private createSupplierMap(suppliers: Record<string, Supplier>): Record<string, Supplier> {
    const supplierMap = {};
    for (const key in suppliers) {
      if (suppliers.hasOwnProperty(key)) {
        supplierMap[key] = suppliers[key];
      }
    }
    return supplierMap;
  }
}
